import { nanoid } from "nanoid";
import {
  uniqueNamesGenerator,
  names,
} from "unique-names-generator";
import { times, shuffle, find } from "lodash";

import { matchPath, matchPlayerPath } from "../firestorePaths";

import { SEAT_POSITIONS } from "../constants";
import {
  MatchPlayer,
  HumanPlayerCount,
  SeatPosition,
  Match,
} from "../types/firestoreTypes";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import { selectMatchPlayers, selectMatches } from "../store/selectors";

// Generate a simple 6-character room code
const generateRoomCode = (): string => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // Removed ambiguous chars I,O,0,1
  let code = "";
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

interface CreateMatchResult {
  matchId: string;
  reservedHumanSeat?: number; // The seat reserved for the first human player
  botPlayers: MatchPlayer[]; // The bot players that were created
}

interface ReturnType {
  createMatch: (arg: { humanPlayerCount: HumanPlayerCount }) => Promise<CreateMatchResult>;
  createQuickBotMatch: () => Promise<CreateMatchResult>;
  createOnlineRoom: () => Promise<CreateMatchResult>;
  findMatchByCode: (code: string) => Match | undefined;
  addBotToMatch: (matchId: string) => Promise<void>;
  removeBotFromMatch: (matchId: string, playerId: string) => Promise<void>;
}

export const useMatches = (): ReturnType => {
  const firestore = useFirestore();
  const matches = useSelector(selectMatches);
  const matchPlayers = useSelector(selectMatchPlayers);

  const createMatch = async ({
    humanPlayerCount,
  }: {
    humanPlayerCount: HumanPlayerCount;
  }): Promise<CreateMatchResult> => {
    let botNames = new Set<string>();

    while (botNames.size < 3) {
      botNames.add(uniqueNamesGenerator({ dictionaries: [names] }));
    }

    const randomisedSeats = shuffle(SEAT_POSITIONS);

    const roomCode = generateRoomCode();
    const matchId = nanoid();
    
    // Create bot players - collect all promises and await them
    const botPromises: Promise<unknown>[] = [];
    const botPlayers: MatchPlayer[] = [];
    times(4 - humanPlayerCount, (index: number) => {
      const playerId = nanoid();
      const player: MatchPlayer = {
        matchId,
        id: playerId,
        human: false,
        name: Array.from(botNames)[index],
        seat: randomisedSeats[index] as SeatPosition,
      };
      botPlayers.push(player);

      const path = matchPlayerPath({ matchId, playerId });
      botPromises.push(firestore.set(path, player));
    });
    
    // Wait for all bots to be created before creating the match
    await Promise.all(botPromises);

    const path = matchPath({ matchId });
    await firestore.set(path, {
      humanPlayerCount,
      name: roomCode, // Use room code as name now
      id: matchId,
      roomCode,
    });
    
    // Return the matchId and the seat reserved for the first human player
    // Bots took seats at indices 0..(numBots-1), so human gets the next one
    const numBots = 4 - humanPlayerCount;
    const reservedHumanSeat = numBots < 4 ? randomisedSeats[numBots] : undefined;
    
    return { matchId, reservedHumanSeat, botPlayers };
  };

  // Quick play with bots - creates match and starts immediately
  const createQuickBotMatch = async (): Promise<CreateMatchResult> => {
    return createMatch({ humanPlayerCount: 1 });
  };

  // Create a room for online play - starts empty (just creator)
  const createOnlineRoom = async (): Promise<CreateMatchResult> => {
    const roomCode = generateRoomCode();
    const matchId = nanoid();

    const path = matchPath({ matchId });
    await firestore.set(path, {
      humanPlayerCount: 4 as HumanPlayerCount,
      name: roomCode,
      id: matchId,
      roomCode,
      isWaitingRoom: true, // Flag to show waiting room UI
    });

    // No reserved seat for online rooms - first come first served
    return { matchId, botPlayers: [] };
  };

  // Find a match by its room code
  const findMatchByCode = (code: string): Match | undefined => {
    if (!matches) return undefined;
    return find(matches, (m: Match) => (m as any).roomCode === code.toUpperCase());
  };

  // Add a bot to an existing match
  const addBotToMatch = async (matchId: string): Promise<void> => {
    const existingPlayers = matchPlayers?.filter((p) => p.matchId === matchId) || [];
    if (existingPlayers.length >= 4) return;

    const occupiedSeats = existingPlayers.map((p) => p.seat);
    const availableSeat = SEAT_POSITIONS.find(
      (s) => !occupiedSeats.includes(s as SeatPosition)
    ) as SeatPosition;

    if (availableSeat === undefined) return;

    const botName = uniqueNamesGenerator({ dictionaries: [names] });
    const playerId = nanoid();
    const player: MatchPlayer = {
      matchId,
      id: playerId,
      human: false,
      name: botName,
      seat: availableSeat,
    };

    const path = matchPlayerPath({ matchId, playerId });
    await firestore.set(path, player);
  };

  // Remove a bot from a match
  const removeBotFromMatch = async (
    matchId: string,
    playerId: string
  ): Promise<void> => {
    const path = matchPlayerPath({ matchId, playerId });
    await firestore.delete(path);
  };

  return {
    createMatch,
    createQuickBotMatch,
    createOnlineRoom,
    findMatchByCode,
    addBotToMatch,
    removeBotFromMatch,
  };
};
